import { useState } from 'react'
import { useCreateRequest } from '../lib/createRequest'
import { Link } from 'react-router-dom'
import { useCustomers, useSaveCustomer } from '../api/queries'
import type { Customer } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { DeleteCustomerCard } from '../components/DeleteCustomerCard'
import { PhoneLink } from '../components/SiteContact'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'

/** Kunderegister med søgning. Projektledere og administratorer kan oprette og rette. */
export function CustomersPage() {
  const { isProjectManager } = useAuth()
  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Customer | 'new' | null>(null)
  useCreateRequest(['kunde'], () => {
    if (isProjectManager) setEditing('new')
  })

  const { data, isLoading, error } = useCustomers(search || undefined, includeInactive)

  return (
    <>
      <div className="row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ maxWidth: 280 }}
          placeholder="Søg på navn, kontakt, by, telefon ..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Vis inaktive
        </label>
        {isProjectManager && (
          <button className="btn btn--primary" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>
            Ny kunde
          </button>
        )}
      </div>

      {editing && (
        <CustomerForm
          customer={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && <EmptyState>Ingen kunder matcher søgningen.</EmptyState>}

      {data && data.length > 0 && (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Kontakt</th>
                  <th>Telefon</th>
                  <th>By</th>
                  <th>Sager</th>
                  {isProjectManager && <th></th>}
                </tr>
              </thead>
              <tbody>
                {data.map((customer) => (
                  <tr key={customer.id} style={{ opacity: customer.isActive ? 1 : 0.55 }}>
                    <td>
                      <Link to={`/customers/${customer.id}`} style={{ fontWeight: 600 }}>
                        {customer.name}
                      </Link>
                      {!customer.isActive && <span className="tiny muted"> (inaktiv)</span>}
                      {customer.reference && <div className="tiny muted">Ref. {customer.reference}</div>}
                    </td>
                    <td className="small">{customer.contactPerson ?? '-'}</td>
                    <td className="small">
                      <PhoneLink phone={customer.phone} />
                    </td>
                    <td className="small muted">{customer.city ?? '-'}</td>
                    <td className="small">
                      {customer.activeProjectCount} aktive
                      <span className="muted"> / {customer.projectCount}</span>
                    </td>
                    {isProjectManager && (
                      <td>
                        <button className="btn btn--sm" onClick={() => setEditing(customer)}>
                          Rediger
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}

/** Opret/rediger kunde. Bruges både fra kundelisten og når en sag oprettes. */
export function CustomerForm({
  customer,
  onClose,
  onSaved
}: {
  customer: Customer | null
  onClose: () => void
  onSaved?: (customer: Customer) => void
}) {
  const { isSuperadmin } = useAuth()
  const save = useSaveCustomer(customer?.id)
  const [form, setForm] = useState({
    name: customer?.name ?? '',
    contactPerson: customer?.contactPerson ?? '',
    email: customer?.email ?? '',
    phone: customer?.phone ?? '',
    address: customer?.address ?? '',
    postalCode: customer?.postalCode ?? '',
    city: customer?.city ?? '',
    reference: customer?.reference ?? '',
    notes: customer?.notes ?? '',
    connectionAmps: customer?.connectionAmps ? String(customer.connectionAmps) : '',
    isActive: customer?.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  function update(field: keyof typeof form, value: string | boolean) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const saved = await save.mutateAsync({
        name: form.name,
        contactPerson: form.contactPerson || null,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
        reference: form.reference || null,
        notes: form.notes || null,
        connectionAmps: form.connectionAmps ? Number(form.connectionAmps) : null,
        isActive: form.isActive
      })
      onSaved?.(saved)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunden kunne ikke gemmes.')
    }
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <Card title={customer ? `Rediger ${customer.name}` : 'Ny kunde'}>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Navn *</span>
              <input required value={form.name} onChange={(e) => update('name', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Kontaktperson</span>
              <input value={form.contactPerson} onChange={(e) => update('contactPerson', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Telefon</span>
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">E-mail</span>
              <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Adresse</span>
              <input value={form.address} onChange={(e) => update('address', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Postnr. og by</span>
              <div className="row">
                <input
                  style={{ maxWidth: 110 }}
                  value={form.postalCode}
                  onChange={(e) => update('postalCode', e.target.value)}
                />
                <input value={form.city} onChange={(e) => update('city', e.target.value)} />
              </div>
            </label>
            <label className="field">
              <span className="field__label">Reference / kundens sagsnr.</span>
              <input value={form.reference} onChange={(e) => update('reference', e.target.value)} />
            </label>
            {/* Hvad installationen paa kundens adresse har ret til hos netselskabet (beslutning 109). */}
            <label className="field">
              <span className="field__label">Rettigheder (A)</span>
              <input
                type="number"
                min={1}
                max={10000}
                inputMode="numeric"
                placeholder="f.eks. 63"
                value={form.connectionAmps}
                onChange={(e) => update('connectionAmps', e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Status</span>
              <select value={form.isActive ? 'true' : 'false'} onChange={(e) => update('isActive', e.target.value === 'true')}>
                <option value="true">Aktiv</option>
                <option value="false">Inaktiv</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span className="field__label">Noter</span>
            <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} />
          </label>

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Gemmer ...' : 'Gem kunde'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Fortryd
            </button>
          </div>
        </form>
      </Card>

      {/* Sletningen står også her, fordi det er i Rediger, man leder efter den - først
          lå den kun nederst på kundesiden, og dér blev den ikke fundet (beslutning 079).
          Kun Superadmin; alle andre sætter Status til Inaktiv ovenfor. */}
      {customer && isSuperadmin && (
        <div style={{ marginTop: 12 }}>
          <DeleteCustomerCard customer={customer} onDeleted={onClose} />
        </div>
      )}
    </div>
  )
}
